import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import Breadcrumb from "@/components/Breadcrumb";
import PageHeader from "@/components/PageHeader";
import { Button, smoothScrollToId } from "@/components/kit";

/* Shared building blocks for legal content pages (privacy & cookies). */

export const P = ({ children, className = "" }) => (
  <p className={`text-[15px] leading-relaxed text-coal/65 ${className}`}>{children}</p>
);

export const Strong = ({ children }) => (
  <strong className="font-semibold text-coal">{children}</strong>
);

export const UL = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2.5 text-[15px] leading-relaxed text-coal/65">
        <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-electric shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export const ExtLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="text-electric hover:underline underline-offset-4">
    {children}
  </a>
);

export const InLink = ({ to, children }) => (
  <Link to={to} className="text-electric hover:underline underline-offset-4">
    {children}
  </Link>
);

export const DataTable = ({ head, rows, testid }) => (
  <div data-testid={testid} className="overflow-x-auto rounded-xl border border-ivory-400">
    <table className="w-full text-left text-sm min-w-[560px]">
      <thead className="bg-ivory-200">
        <tr>
          {head.map((h) => (
            <th key={h} className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-coal/50 font-medium whitespace-nowrap">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-ivory-300 bg-white">
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td key={j} className={`px-4 py-3 align-top text-coal/70 ${j === 0 ? "font-medium text-coal whitespace-nowrap" : ""}`}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const InfoGrid = ({ items }) => (
  <div className="rounded-xl border border-ivory-400 bg-ivory-100 p-5 grid sm:grid-cols-2 gap-x-8 gap-y-4">
    {items.map(({ label, value }) => (
      <div key={label}>
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-coal/45 mb-1">{label}</div>
        <div className="text-sm font-medium text-coal">{value}</div>
      </div>
    ))}
  </div>
);

const LegalLayout = ({ testid, crumbLabel, crumbHref, kicker, italic, bold, lede, updated, sections }) => {
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    smoothScrollToId(id);
  };

  return (
    <main data-testid={testid} className="bg-ivory min-h-screen pt-28 lg:pt-32">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Breadcrumb items={[{ label: "Home", href: "/" }, { label: crumbLabel, href: crumbHref }]} />
        <PageHeader kicker={kicker} italic={italic} bold={bold} lede={lede} />
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <aside className="hidden lg:block lg:col-span-3 sticky top-28">
            <div className="rounded-2xl border border-ivory-400 bg-white p-5 shadow-card-light">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-coal/45 mb-4">Inhoud</div>
              <ol className="space-y-1">
                {sections.map((s, idx) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      onClick={scrollTo(s.id)}
                      data-testid={`legal-toc-${s.id}`}
                      className="group flex items-baseline gap-3 py-1.5 text-sm text-coal/60 hover:text-electric transition-colors"
                    >
                      <span className="font-mono text-[10px] text-electric/70">{String(idx + 1).padStart(2, "0")}</span>
                      <span className="leading-snug">{s.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
              <div className="mt-5 pt-4 border-t border-ivory-300 flex items-center gap-2 text-xs text-coal/50">
                <CalendarDays className="w-3.5 h-3.5 text-electric" />
                Laatst bijgewerkt: {updated}
              </div>
            </div>
          </aside>

          <div className="lg:col-span-9 space-y-6">
            <div className="lg:hidden inline-flex items-center gap-2 text-xs text-coal/50 rounded-full border border-ivory-400 bg-white px-4 py-2">
              <CalendarDays className="w-3.5 h-3.5 text-electric" />
              Laatst bijgewerkt: {updated}
            </div>

            {sections.map((s, idx) => (
              <motion.section
                key={s.id}
                id={s.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5 }}
                data-testid={`legal-section-${s.id}`}
                className="scroll-mt-28 rounded-[1.5rem] border border-ivory-400 bg-white p-7 lg:p-9 shadow-card-light"
              >
                <div className="flex items-baseline gap-4 mb-5">
                  <span className="font-mono text-xs text-electric">{String(idx + 1).padStart(2, "0")}</span>
                  <h2 className="font-display text-xl lg:text-2xl font-bold text-coal text-balance">{s.title}</h2>
                </div>
                <div className="space-y-4 lg:pl-10">{s.content}</div>
              </motion.section>
            ))}

            <div className="rounded-[1.5rem] bg-obsidian text-ivory p-8 lg:p-10 relative overflow-hidden">
              <div className="glow-orb w-[320px] h-[320px] bg-electric/25 -top-16 right-8" />
              <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                <div className="lg:col-span-8">
                  <h2 className="font-display text-2xl lg:text-3xl font-black tracking-tight text-ivory text-balance">
                    Vragen over jouw gegevens?
                  </h2>
                  <p className="mt-2 text-white/60">
                    Mail naar info@uppit.nl of stuur Tim een appje — je krijgt snel en persoonlijk antwoord.
                  </p>
                </div>
                <div className="lg:col-span-4 lg:text-right">
                  <Button to="/contact" variant="primary" withArrow testid="legal-contact-cta">
                    Naar contact
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default LegalLayout;
